import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { fmtFecha, fmtMoney, fmtHora, localDate } from '../utils/format';
import '../styles/Cajas.css';

/* ── Iconos SVG inline (react-icons da cuadros en algunas maquinas) ── */
const svgProps = (size) => ({
  xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24',
  width: size, height: size, fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  style: { display: 'block' },
});
const IconSearch = ({ size = 13 }) => (
  <svg {...svgProps(size)}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconEye = ({ size = 13 }) => (
  <svg {...svgProps(size)}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconPrinter = ({ size = 13 }) => (
  <svg {...svgProps(size)}>
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const IconX = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const today = () => localDate();
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/* ── Modal de detalle del cuadre ──────────────────────────────────────── */
function CuadreModal({ sesionId, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/api/cajas/sesiones/${sesionId}/summary`)
      .then(r => setData(r.data.data))
      .catch(() => setData(null));
  }, [sesionId]);

  if (!data) return (
    <div className="cj-overlay">
      <div className="cj-det-modal">
        <div className="cj-det-header">
          <span>Detalle del cuadre</span>
          <button className="cj-modal-close" onClick={onClose}><IconX size={16} /></button>
        </div>
        <div className="cj-det-body" style={{ justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Cargando...</span>
        </div>
      </div>
    </div>
  );

  const { sesion, movimientos, monto_apertura, ventas_efectivo, ingresos,
          ingresos_digitales, egresos, total_efectivo, ventas_por_forma_pago, ventas } = data;

  const diferencia = sesion.diferencia != null ? parseFloat(sesion.diferencia) : null;

  return (
    <div className="cj-overlay">
      <div className="cj-det-modal">
        {/* Header */}
        <div className="cj-det-header">
          <div>
            <span style={{ fontWeight: 800, fontSize: 14 }}>{sesion.caja_nombre}</span>
            <span style={{ marginLeft: 12, fontSize: 12, color: '#94a3b8' }}>
              {fmtFecha(sesion.fecha)}
            </span>
            {sesion.almacen_nombre && (
              <span style={{ marginLeft: 10, fontSize: 11.5, color: '#64748b' }}>
                · {sesion.almacen_nombre}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={`cj-badge ${sesion.estado === 'ABIERTA' ? 'cj-badge-open' : 'cj-badge-closed'}`}>
              {sesion.estado}
            </span>
            <button className="cj-modal-close" onClick={onClose}><IconX size={16} /></button>
          </div>
        </div>

        {/* Sub-header: horario */}
        <div className="cj-det-subheader">
          <span>Apertura: <strong>{fmtHora(sesion.abierto_en)}</strong></span>
          <span style={{ color: '#94a3b8', margin: '0 8px' }}>→</span>
          <span>Cierre: <strong>{sesion.cerrado_en ? fmtHora(sesion.cerrado_en) : '—'}</strong></span>
        </div>

        <div className="cj-det-body">

          {/* ── VENTAS ── */}
          <div className="cj-det-section">
            <div className="cj-det-section-title">Ventas de la sesión</div>

            {(ventas_por_forma_pago || []).length > 0 && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, padding: '4px 12px' }}>
                  Contado — por forma de pago
                </div>
                {ventas_por_forma_pago.map(fp => (
                  <div key={fp.abreviatura} className="cj-det-row">
                    <span className="cj-det-label" style={{ paddingLeft: 12 }}>
                      <span className={`cj-det-fp-badge ${fp.abreviatura === 'EFECTIVO' ? 'cj-det-fp-ef' : 'cj-det-fp-dig'}`}>
                        {fp.forma_pago}
                      </span>
                      <span style={{ color: '#94a3b8', marginLeft: 6 }}>({fp.nro} vta{fp.nro !== 1 ? 's' : ''})</span>
                    </span>
                    <span className="cj-det-value">S/ {fmtMoney(fp.monto)}</span>
                  </div>
                ))}
                <div className="cj-det-row cj-det-subtotal">
                  <span className="cj-det-label">Subtotal Contado</span>
                  <span className="cj-det-value">S/ {fmtMoney(ventas?.total_contado)}</span>
                </div>
              </div>
            )}

            <div className="cj-det-row">
              <span className="cj-det-label">
                Crédito / Mixto
                {parseFloat(ventas?.total_credito || 0) > 0 && (
                  <span style={{ fontSize: 10.5, color: '#64748b', marginLeft: 6 }}>(cuota futura en CxC)</span>
                )}
              </span>
              <span className="cj-det-value" style={{ color: '#7c3aed' }}>
                S/ {fmtMoney(ventas?.total_credito)}
              </span>
            </div>

            <div className="cj-det-row cj-det-total-row">
              <span className="cj-det-label" style={{ fontWeight: 700 }}>
                Total Ventas
                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
                  {ventas?.nro_ventas} vta(s){ventas?.nro_anuladas > 0 && ` / ${ventas.nro_anuladas} anulada(s)`}
                </span>
              </span>
              <span className="cj-det-value" style={{ color: '#1d4ed8', fontWeight: 800, fontSize: 14 }}>
                S/ {fmtMoney(ventas?.total_ventas)}
              </span>
            </div>
          </div>

          {/* ── MOVIMIENTOS DE CAJA ── */}
          {(movimientos || []).length > 0 && (
            <div className="cj-det-section">
              <div className="cj-det-section-title">Movimientos de caja</div>
              <table className="cj-det-movs-table">
                <thead>
                  <tr>
                    <th>Hora</th>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th>F. Pago</th>
                    <th style={{ textAlign: 'right' }}>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => (
                    <tr key={m.id}>
                      <td style={{ color: '#94a3b8' }}>{fmtHora(m.creado_en)}</td>
                      <td>
                        <span style={{
                          padding: '1px 7px', borderRadius: 8, fontSize: 10.5, fontWeight: 700,
                          background: m.tipo === 'INGRESO' ? '#dcfce7' : '#fee2e2',
                          color:      m.tipo === 'INGRESO' ? '#166534' : '#991b1b',
                        }}>{m.tipo}</span>
                      </td>
                      <td style={{ color: '#1e293b' }}>{m.concepto || '—'}</td>
                      <td style={{ color: '#64748b', fontSize: 11 }}>{m.forma_pago || (m.es_efectivo ? 'Efectivo' : '—')}</td>
                      <td style={{
                        textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: m.tipo === 'INGRESO' ? '#16a34a' : '#dc2626',
                      }}>
                        {m.tipo === 'INGRESO' ? '+' : '-'} S/ {fmtMoney(m.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── CUADRE DE EFECTIVO ── */}
          <div className="cj-det-section">
            <div className="cj-det-section-title">Cuadre de efectivo</div>

            <div className="cj-det-row">
              <span className="cj-det-label">+ Monto apertura</span>
              <span className="cj-det-value">S/ {fmtMoney(monto_apertura)}</span>
            </div>
            <div className="cj-det-row">
              <span className="cj-det-label">+ Ventas efectivo (contado)</span>
              <span className="cj-det-value" style={{ color: '#16a34a' }}>S/ {fmtMoney(ventas_efectivo)}</span>
            </div>
            <div className="cj-det-row">
              <span className="cj-det-label">+ Ingresos efectivo</span>
              <span className="cj-det-value" style={{ color: '#16a34a' }}>S/ {fmtMoney(ingresos)}</span>
            </div>
            {parseFloat(ingresos_digitales || 0) > 0 && (
              <div className="cj-det-row" style={{ opacity: 0.75 }}>
                <span className="cj-det-label" style={{ color: '#2563eb' }}>
                  Ingresos digitales <span style={{ fontSize: 10 }}>(no suman al cajón)</span>
                </span>
                <span className="cj-det-value" style={{ color: '#2563eb' }}>S/ {fmtMoney(ingresos_digitales)}</span>
              </div>
            )}
            <div className="cj-det-row">
              <span className="cj-det-label">- Egresos</span>
              <span className="cj-det-value" style={{ color: '#dc2626' }}>S/ {fmtMoney(egresos)}</span>
            </div>

            <div className="cj-det-total-box">
              <span>Total esperado en caja</span>
              <span style={{ fontSize: 17, fontWeight: 800 }}>S/ {fmtMoney(total_efectivo)}</span>
            </div>

            {sesion.monto_cierre != null && (
              <>
                <div className="cj-det-row" style={{ marginTop: 4 }}>
                  <span className="cj-det-label">Monto declarado al cierre</span>
                  <span className="cj-det-value">S/ {fmtMoney(sesion.monto_cierre)}</span>
                </div>
                {diferencia !== null && (
                  <div className="cj-det-row">
                    <span className="cj-det-label" style={{ fontWeight: 700 }}>Diferencia</span>
                    <span className={`cj-det-value ${diferencia > 0 ? 'cj-dif-pos' : diferencia < 0 ? 'cj-dif-neg' : 'cj-dif-zer'}`}>
                      {diferencia >= 0 ? '+' : ''}S/ {fmtMoney(diferencia)}
                    </span>
                  </div>
                )}
              </>
            )}

            {(sesion.observacion_apertura || sesion.observacion_cierre) && (
              <div style={{ padding: '8px 12px', borderTop: '1px solid #f1f5f9' }}>
                {sesion.observacion_apertura && (
                  <p style={{ fontSize: 11.5, color: '#64748b', margin: '2px 0' }}>
                    <strong>Obs. apertura:</strong> {sesion.observacion_apertura}
                  </p>
                )}
                {sesion.observacion_cierre && (
                  <p style={{ fontSize: 11.5, color: '#64748b', margin: '2px 0' }}>
                    <strong>Obs. cierre:</strong> {sesion.observacion_cierre}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="cj-modal-footer">
          <button
            className="cj-btn-save"
            style={{ background: '#1e3a5f', display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => window.open(`/print-cuadre/${sesionId}`, '_blank')}
          >
            <IconPrinter size={13} /> Imprimir A4
          </button>
          <button className="cj-btn-cancel" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Página principal ──────────────────────────────────────────────────── */
export default function CajaHistorialPage() {
  const [rows,    setRows]    = useState([]);
  const [cajas,   setCajas]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [modalId, setModalId] = useState(null);

  const [filters, setFilters] = useState({
    desde:   firstOfMonth(),
    hasta:   today(),
    caja_id: '',
  });

  const loadCajas = useCallback(async () => {
    try {
      const r = await api.get('/api/cajas');
      setCajas(r.data.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadCajas(); }, [loadCajas]);

  const search = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (filters.desde)   params.desde   = filters.desde;
      if (filters.hasta)   params.hasta   = filters.hasta;
      if (filters.caja_id) params.caja_id = filters.caja_id;
      const r = await api.get('/api/cajas/historial', { params });
      setRows(r.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { search(); }, [search]);

  const dif = (row) => parseFloat(row.diferencia ?? 0);

  return (
    <div className="cj-page">
      {/* Toolbar */}
      <div className="cj-toolbar">
        <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
          Historial de Cuadres
        </span>
      </div>

      {/* Filter bar */}
      <div className="cj-filter-bar">
        <span className="cj-filter-label">Desde:</span>
        <input
          type="date"
          className="cj-filter-input"
          value={filters.desde}
          onChange={e => setFilters(p => ({ ...p, desde: e.target.value }))}
        />
        <span className="cj-filter-label">Hasta:</span>
        <input
          type="date"
          className="cj-filter-input"
          value={filters.hasta}
          onChange={e => setFilters(p => ({ ...p, hasta: e.target.value }))}
        />
        <span className="cj-filter-label">Caja:</span>
        <select
          className="cj-filter-select"
          value={filters.caja_id}
          onChange={e => setFilters(p => ({ ...p, caja_id: e.target.value }))}
        >
          <option value="">Todas</option>
          {cajas.map(c => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        <button className="cj-filter-btn" onClick={search}>
          <IconSearch size={12} /> Buscar
        </button>
        <span className="cj-count" style={{ marginLeft: 'auto' }}>
          {rows.length} sesión(es)
        </span>
      </div>

      {/* Table */}
      <div className="cj-table-wrap">
        {loading ? (
          <div className="cj-loading">Cargando...</div>
        ) : error ? (
          <div className="cj-error">{error}</div>
        ) : (
          <table className="cj-hist-table">
            <thead>
              <tr>
                <th className="cj-hist-th" style={{ width: 150 }}>Caja</th>
                <th className="cj-hist-th" style={{ width: 90 }}>Fecha</th>
                <th className="cj-hist-th" style={{ width: 75 }}>Apertura</th>
                <th className="cj-hist-th" style={{ width: 75 }}>Cierre</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 85 }}>Apertura S/</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 95 }}>Vtas. Contado</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 95 }}>Vtas. Crédito</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 85 }}>Ingresos Ef.</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 80 }}>Egresos</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 90 }}>Total Ef.</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 90 }}>Mto. Cierre</th>
                <th className="cj-hist-th cj-hist-th-right" style={{ width: 85 }}>Diferencia</th>
                <th className="cj-hist-th cj-hist-th-center" style={{ width: 80 }}>Estado</th>
                <th className="cj-hist-th cj-hist-th-center" style={{ width: 80 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="cj-empty">
                    Sin sesiones en el período seleccionado
                  </td>
                </tr>
              ) : rows.map((row, i) => {
                const ventasEfec = parseFloat(row.total_ventas_efectivo || row.total_ventas_contado || 0);
                const totalEfec  = parseFloat(row.monto_apertura || 0)
                  + ventasEfec
                  + parseFloat(row.total_ingresos || 0)
                  - parseFloat(row.total_egresos || 0);
                const d = dif(row);

                return (
                  <tr key={row.id} className={i % 2 === 0 ? 'cj-tr-even' : 'cj-tr-odd'}>
                    <td className="cj-td">
                      <span className="cj-nombre">{row.caja_nombre}</span>
                      {row.almacen_nombre && (
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.almacen_nombre}</div>
                      )}
                    </td>
                    <td className="cj-td">{fmtFecha(row.fecha)}</td>
                    <td className="cj-td" style={{ fontSize: 11.5, color: '#64748b' }}>{fmtHora(row.abierto_en)}</td>
                    <td className="cj-td" style={{ fontSize: 11.5, color: '#64748b' }}>
                      {row.cerrado_en ? fmtHora(row.cerrado_en) : '—'}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right">S/ {fmtMoney(row.monto_apertura)}</td>
                    <td className="cj-hist-td cj-hist-td-right" style={{ color: '#2563eb' }}>
                      S/ {fmtMoney(row.total_ventas_contado)}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right" style={{ color: '#7c3aed' }}>
                      {parseFloat(row.total_ventas_credito || 0) > 0
                        ? `S/ ${fmtMoney(row.total_ventas_credito)}`
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right" style={{ color: '#16a34a' }}>
                      S/ {fmtMoney(row.total_ingresos)}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right" style={{ color: '#dc2626' }}>
                      S/ {fmtMoney(row.total_egresos)}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right" style={{ fontWeight: 700 }}>
                      S/ {fmtMoney(totalEfec)}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right">
                      {row.monto_cierre != null ? `S/ ${fmtMoney(row.monto_cierre)}` : '—'}
                    </td>
                    <td className="cj-hist-td cj-hist-td-right">
                      {row.diferencia != null ? (
                        <span className={d > 0 ? 'cj-dif-pos' : d < 0 ? 'cj-dif-neg' : 'cj-dif-zer'}>
                          {d >= 0 ? '+' : ''}S/ {fmtMoney(d)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="cj-hist-td cj-hist-td-center">
                      <span className={`cj-badge ${row.estado === 'ABIERTA' ? 'cj-badge-open' : 'cj-badge-closed'}`}>
                        {row.estado}
                      </span>
                    </td>
                    <td className="cj-hist-td cj-hist-td-center">
                      <div className="cj-hist-actions">
                        <button
                          className="cj-btn-hist-ver"
                          title="Ver detalle del cuadre"
                          onClick={() => setModalId(row.id)}
                        >
                          <IconEye size={13} /> Ver
                        </button>
                        <button
                          className="cj-btn-hist-imp"
                          title="Imprimir cuadre A4"
                          onClick={() => window.open(`/print-cuadre/${row.id}`, '_blank')}
                        >
                          <IconPrinter size={13} /> A4
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="cj-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {modalId && (
        <CuadreModal sesionId={modalId} onClose={() => setModalId(null)} />
      )}
    </div>
  );
}
